export interface User {
  id: number;
  email: string;
  name: string;
  isAdmin: boolean;
  /** Edzői jogok. Egy usernek egyszerre több szerepe is lehet. */
  isCoach: boolean;
  /** Van saját, a fiókjához linkelt Player rekordja. */
  isPlayer: boolean;
  isParent: boolean;
  /** Vendég: a klubnaptárat nézheti, de nem szerkeszthet semmit. */
  isGuest: boolean;
  /** Vendégnél a képviselt külsős csapat neve; üres = belső kiszolgáló. */
  guestTeam?: string | null;
  isActive?: boolean;
  createdAt: string;
  ageGroups?: string | null;
  ageGroupIds?: number[];
}

// Helper functions to check user roles. Egy usernek egyszerre több is lehet
// igaz (pl. Edző ÉS Játékos) - ezért mindegyik a saját flagjét nézi
// függetlenül, nem egy kizárólagos szerepkör-mezőt.
export const isAdmin = (user: User | null): boolean => {
  return user?.isAdmin === true;
};

export const isCoach = (user: User | null): boolean => {
  return user?.isCoach === true;
};

export const isParent = (user: User | null): boolean => {
  return user?.isParent === true;
};

export const isPlayer = (user: User | null): boolean => {
  return user?.isPlayer === true;
};

export const isStaff = (user: User | null): boolean => {
  return isAdmin(user) || isCoach(user);
};

export const isGuest = (user: User | null): boolean => {
  return user?.isGuest === true;
};

/**
 * Csak vendég jog: nincs mellette semmilyen más képesség. A felület ez alapján
 * szűkül le a naptárra - akinek emellett edzői joga is van, az mindent lát,
 * amit edzőként szokott.
 */
export const isGuestOnly = (user: User | null): boolean =>
  isGuest(user) && !isAdmin(user) && !isCoach(user) && !isPlayer(user) && !isParent(user);

/**
 * Családi nézet: játékos vagy szülő, stáb jog nélkül. Ők egy egyszerűsített,
 * mobilappszerű felületet kapnak (Kezdőlap, Program, Hírek, Én) - a stáb
 * eszközei és a statisztikák nélkül.
 */
export const isFamilyUser = (user: User | null): boolean =>
  (isPlayer(user) || isParent(user)) && !isStaff(user);

/**
 * A user aktív szerepeinek felsorolása, vesszővel elválasztva (pl. "Edző,
 * Játékos"). Egyszerre több is igaz lehet, ezért nem egyetlen címke, hanem
 * lista - a menüben/profilban ez jelenik meg a régi, kizárólagos
 * "Staff"/"External" felirat helyett.
 */
export const describeRoles = (user: User | null): string => {
  if (!user) return '';

  const labels: string[] = [];
  if (isAdmin(user)) labels.push('Admin');
  if (isCoach(user)) labels.push('Edző');
  if (isPlayer(user)) labels.push('Játékos');
  if (isParent(user)) labels.push('Szülő');
  if (isGuest(user)) labels.push(user?.guestTeam ? `Vendég (${user.guestTeam})` : 'Vendég');

  return labels.length > 0 ? labels.join(', ') : 'Felhasználó';
};

export interface AuthResponse {
  token: string;
  user: User;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Kész munkamenet átvétele (pl. meghívó elfogadása után). */
  applySession: (session: AuthResponse) => void;
  refreshUser: () => Promise<void>;
  loading: boolean;
}